import { Check, X } from "lucide-react";
import styled from "styled-components";
import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";

const Progress = styled.div`
	grid-column: 1 / -1;
	border-top: 1px solid #edf0f5;
	padding-top: 12px;
	display: flex;
	align-items: center;
	gap: 10px;
	flex-wrap: wrap;
	font-size: 12px;
	line-height: 20px;
	.step { display: inline-flex; align-items: center; gap: 6px; color: #98a2b3; }
	.marker { display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; width: 20px; height: 20px; border: 1px solid #d0d5dd; border-radius: 50%; font-size: 10px; flex-shrink: 0; }
	.done { color: #067647; }
	.done .marker { background: #ecfdf3; border-color: #abefc6; }
	.current { color: #175cd3; font-weight: 600; }
	.current .marker { background: #eff4ff; border-color: #b2ccff; }
	.current.done { color: #067647; }
	.current.done .marker { background: #ecfdf3; border-color: #abefc6; }
	.processing { color: #b54708; }
	.processing .marker { background: #fffaeb; border-color: #fedf89; }
	.rejected { color: #b42318; font-weight: 600; margin-left: 8px; }
	.rejected .marker { background: #fef3f2; border-color: #fecdca; }
	.connector { width: 24px; height: 1px; background: #e4e7ec; }
	.connector.done { background: #75e0a7; }
`;

const CompactProgress = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 8px;
	white-space: nowrap;
	.track { display: inline-flex; align-items: center; }
	.dot { display: inline-flex; align-items: center; justify-content: center; box-sizing: border-box; width: 12px; height: 12px; border: 1.5px solid #d0d5dd; border-radius: 50%; background: #fff; flex-shrink: 0; }
	.dot.done { border-color: #12b76a; background: #12b76a; }
	.dot.current { border-color: #175cd3; background: #eff4ff; }
	.dot.current.processing { border-color: #f79009; background: #fffaeb; }
	.dot.rejected { width: 14px; height: 14px; border-color: #f04438; background: #f04438; color: #fff; }
	.line { width: 14px; height: 2px; background: #e4e7ec; }
	.line.done { background: #12b76a; }
	.label { font-size: 12px; font-weight: 500; line-height: 18px; }
`;

const compactLabelColor: Record<string, string> = { created: "#175cd3", processing: "#b54708", completed: "#067647", rejected: "#b42318" };

/** One-line roadmap for list rows: the same stages as StatementProgress, sized to fit a table cell. */
export const StatementProgressCompact = ({ status, label }: { status: string; label: string }) => {
	const normalizedStatus = status.trim().toLowerCase();
	const stage = ["created", "processing", "completed"].indexOf(normalizedStatus);
	const rejected = normalizedStatus === "rejected";
	// A rejected statement shows the stages it passed (created) and stops with a red cross; any other unknown status shows an empty track.
	const steps = rejected ? 2 : 3;
	return (
		<CompactProgress title={label}>
			<span className="track" aria-hidden="true">
				{Array.from({ length: steps }, (_, index) => {
					const isRejectedStep = rejected && index === steps - 1;
					const done = !isRejectedStep && (rejected || (stage >= 0 && (index < stage || stage === 2)));
					const current = !rejected && index === stage && stage < 2;
					return <span key={index} style={{ display: "inline-flex", alignItems: "center" }}>
						{index > 0 && <span className={`line${done || isRejectedStep || (stage >= 0 && index <= stage) ? " done" : ""}`} style={isRejectedStep ? { background: "#f04438" } : undefined} />}
						<span className={`dot${done ? " done" : ""}${current ? " current" : ""}${current && stage === 1 ? " processing" : ""}${isRejectedStep ? " rejected" : ""}`}>
							{isRejectedStep && <X size={9} strokeWidth={3} />}
						</span>
					</span>;
				})}
			</span>
			<span className="label" style={{ color: compactLabelColor[normalizedStatus] ?? "#667085" }}>{label}</span>
		</CompactProgress>
	);
};

/** Reflects the current API status, not a timestamped transition history. */
export const StatementProgress = ({ status }: { status: string }) => {
	useLocale();
	const normalizedStatus = status.trim().toLowerCase();
	const stage = ["created", "processing", "completed"].indexOf(normalizedStatus);
	const rejected = normalizedStatus === "rejected";
	const labels = [msg("created"), msg("processing"), msg("completed")];
	return (
		<Progress>
			{labels.map((label, index) => {
				const done = stage >= 0 && (index < stage || stage === 2);
				const current = index === stage;
				return (
					<span key={index} style={{ display: "inline-flex", alignItems: "center", gap: "10px" }}>
						{index > 0 && <span aria-hidden="true" className={`connector${stage >= 0 && index <= stage ? " done" : ""}`} />}
						<span className={`step${done ? " done" : ""}${current ? " current" : ""}${current && stage === 1 ? " processing" : ""}`} aria-current={current ? "step" : undefined}>
							<span className="marker" aria-hidden="true">{done ? <Check size={12} /> : index + 1}</span>
							{label}
						</span>
					</span>
				);
			})}
			{rejected && <span className="step rejected" aria-current="step"><span className="marker" aria-hidden="true"><X size={12} /></span>{msg("rejected")}</span>}
		</Progress>
	);
};
